import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  TrendingUp, 
  Target, 
  Clock, 
  Trophy, 
  AlertCircle,
  CheckCircle,
  Zap,
  Calendar
} from "lucide-react";
import { useTrialManagement } from "@/hooks/useTrialManagement";
import { useWorkspace } from "@/hooks/useWorkspace";

export const TrialDashboard: React.FC = () => {
  const { currentWorkspace } = useWorkspace();
  const { trialAnalysis, milestones, isLoadingAnalysis, isLoadingMilestones } = useTrialManagement();

  if (!currentWorkspace?.trial_expires_at) {
    return null;
  }

  const daysLeft = Math.ceil(
    (new Date(currentWorkspace.trial_expires_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  const completedMilestones = milestones?.filter(m => m.is_completed).length || 0;
  const totalMilestones = milestones?.length || 0;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Days Remaining</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{Math.max(0, daysLeft)}</div>
            <p className="text-xs text-muted-foreground">
              Trial expires {new Date(currentWorkspace.trial_expires_at).toLocaleDateString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Engagement Score</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {isLoadingAnalysis ? '...' : Math.round(trialAnalysis?.engagement_score || 0)}%
            </div>
            <Progress value={trialAnalysis?.engagement_score || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Probability</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {isLoadingAnalysis ? '...' : Math.round((trialAnalysis?.conversion_probability || 0) * 100)}%
            </div>
            <Badge 
              variant={trialAnalysis?.risk_level === 'low' ? 'default' : 
                      trialAnalysis?.risk_level === 'medium' ? 'secondary' : 'destructive'}
              className="mt-2"
            >
              {trialAnalysis?.risk_level || 'unknown'} risk
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Milestones</CardTitle>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {completedMilestones}/{totalMilestones}
            </div>
            <p className="text-xs text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="progress" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="progress">Progress</TabsTrigger>
          <TabsTrigger value="milestones">Milestones</TabsTrigger>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
        </TabsList>

        <TabsContent value="progress" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Trial Progress Overview
              </CardTitle>
              <CardDescription>
                Track your journey to becoming a power user
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Overall Engagement</span>
                  <span>{Math.round(trialAnalysis?.engagement_score || 0)}%</span>
                </div>
                <Progress value={trialAnalysis?.engagement_score || 0} className="h-3" />
              </div>

              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Feature Discovery</span>
                  <span>{Math.round((completedMilestones / Math.max(1, totalMilestones)) * 100)}%</span>
                </div>
                <Progress 
                  value={(completedMilestones / Math.max(1, totalMilestones)) * 100} 
                  className="h-3" 
                />
              </div>

              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Time Utilization</span>
                  <span>{Math.max(0, 100 - (daysLeft / 14) * 100).toFixed(0)}%</span>
                </div>
                <Progress 
                  value={Math.max(0, 100 - (daysLeft / 14) * 100)} 
                  className="h-3" 
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="milestones" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Achievement Milestones
              </CardTitle>
              <CardDescription>
                Complete these milestones to unlock the full potential
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingMilestones ? (
                <div className="text-center py-8 text-muted-foreground">Loading milestones...</div>
              ) : milestones && milestones.length > 0 ? (
                <div className="space-y-4">
                  {milestones.map((milestone) => (
                    <div 
                      key={milestone.milestone_id}
                      className="flex items-center justify-between p-4 rounded-lg border bg-card"
                    >
                      <div className="flex items-center gap-3">
                        {milestone.is_completed ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
                        )}
                        <div>
                          <h4 className="font-medium">{milestone.milestone_name}</h4>
                          <p className="text-sm text-muted-foreground">
                            Progress: {Math.round(milestone.progress_percentage)}%
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Progress 
                          value={milestone.progress_percentage} 
                          className="w-20 h-2" 
                        />
                        {milestone.is_completed && (
                          <Badge variant="default" className="mt-1 text-xs">
                            Completed
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No milestones available yet
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recommendations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Smart Recommendations
              </CardTitle>
              <CardDescription>
                AI-powered suggestions to maximize your trial success
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trialAnalysis?.recommended_actions && trialAnalysis.recommended_actions.length > 0 ? (
                <div className="space-y-3">
                  {trialAnalysis.recommended_actions.map((action, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-3 p-4 rounded-lg bg-accent/50 border border-accent"
                    >
                      <AlertCircle className="h-5 w-5 text-primary mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium">{action}</p>
                      </div>
                      <Button size="sm" variant="outline">
                        Take Action
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {isLoadingAnalysis ? 'Loading recommendations...' : 'No recommendations available'}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};